/**
 * /accounting/sales-tax — Sales Tax Liability Center (Phase 10).
 *
 * Read-only view of the 2310 ledger grouped by jurisdiction + period, with
 * actions to record an accrual (CR 2310) or a remittance (DR 2310). Foundation
 * for full invoice-line tax collection in Phase 11.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Plus, Minus } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useSeedGeneralLedger } from "@/data/accounting/useSeedGeneralLedger";
import { FL_JURISDICTIONS, taxMemo, totalRate } from "@/data/accounting/tax/salesTaxConfig";
import { summarizeSalesTax } from "@/data/accounting/tax/salesTaxLedger";

const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;
const today = () => new Date().toISOString().slice(0, 10);

export default function SalesTaxCenterPage() {
  useSeedGeneralLedger();
  const { journalEntries, postJournal } = useGeneralLedger();

  const summary = useMemo(() => summarizeSalesTax(journalEntries), [journalEntries]);

  const [accrualOpen, setAccrualOpen] = useState(false);
  const [remitOpen, setRemitOpen] = useState(false);
  const [remitJurisdiction, setRemitJurisdiction] = useState<string>("");

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/accounting/gl" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="h-3 w-3" /> Back to General Ledger
          </Link>
          <h1 data-tour-id="sales-tax-summary" className="text-2xl font-semibold tracking-tight">Sales Tax Liability</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live from <code>2310 Accrued Sales Tax</code>. Florida state 6% + county discretionary surtax.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setAccrualOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Manual accrual
          </Button>
          <Button size="sm" onClick={() => { setRemitJurisdiction(summary.byJurisdiction[0]?.jurisdictionId ?? FL_JURISDICTIONS[0].id); setRemitOpen(true); }}>
            <Minus className="h-3.5 w-3.5 mr-1" /> Record remittance
          </Button>
        </div>
      </div>

      <CockpitSection>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Kpi label="Open balance" value={fmt(summary.openBalance)} tone={summary.openBalance > 0 ? "text-amber-600 dark:text-amber-500" : ""} />
          <Kpi label="Lifetime collected" value={fmt(summary.totalAccrued)} />
          <Kpi label="Lifetime remitted" value={fmt(summary.totalRemitted)} />
          <Kpi
            label="Jurisdictions w/ balance"
            value={String(summary.byJurisdiction.filter(j => j.balance > 0.005).length)}
            sub={`${summary.byJurisdiction.length} active`}
          />
        </div>

        {summary.unassigned.balance > 0.005 && (
          <div className="mb-4 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-500" />
            <div>
              <div className="font-medium">{fmt(summary.unassigned.balance)} unassigned to a jurisdiction</div>
              <div className="text-xs text-muted-foreground">
                Future tax postings should tag a memo like <code>tax:FL-HILLSBOROUGH</code> so this surface can attribute liability.
              </div>
            </div>
          </div>
        )}

        <div className="rounded-md border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Jurisdiction</th>
                <th className="text-right px-3 py-2">Rate</th>
                <th className="text-right px-3 py-2">Collected</th>
                <th className="text-right px-3 py-2">Remitted</th>
                <th className="text-right px-3 py-2">Open</th>
                <th className="text-left px-3 py-2">Oldest open</th>
                <th className="text-right px-3 py-2">Cadence</th>
              </tr>
            </thead>
            <tbody>
              {FL_JURISDICTIONS.map(j => {
                const row = summary.byJurisdiction.find(b => b.jurisdictionId === j.id);
                const balance = row?.balance ?? 0;
                return (
                  <tr key={j.id} className="border-t border-border hover:bg-accent/30">
                    <td className="px-3 py-2 font-medium">{j.countyName} County</td>
                    <td className="px-3 py-2 text-right tabular-nums">{(totalRate(j) * 100).toFixed(2)}%</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(row?.collected ?? 0)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmt(row?.remitted ?? 0)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${balance > 0.005 ? "text-amber-600 dark:text-amber-500" : ""}`}>
                      {fmt(balance)}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{row?.oldestOpenPeriod ?? "—"}</td>
                    <td className="px-3 py-2 text-right text-xs capitalize">{j.cadence}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
          Note: Florida home-improvement contractors performing real-property improvements are generally end consumers
          of materials (tax paid to supplier; not collected from customer). Tangible-personal-property sales (appliance
          retail, parts sold without install) require collection. Use Manual Accrual for retail tax exposure until
          Phase 11 wires invoice-line tax collection automatically.
        </p>
      </CockpitSection>

      <AccrualDialog
        open={accrualOpen}
        onClose={() => setAccrualOpen(false)}
        onPost={(input) => {
          postJournal({
            postingDate: input.date,
            description: `Sales tax accrual — ${input.jurisdictionLabel}`,
            source: "sales_tax",
            sourceRef: { kind: "sales_tax_accrual", id: `acc_${Date.now()}` },
            createdBy: "Steve",
            lines: [
              // Reclassify: pull tax portion OUT of the revenue/deferred account it landed in,
              // and book it as a liability. Cash stays where it is (was already booked at receipt).
              { accountId: input.offsetAccountId, side: "debit", amount: input.amount, memo: `Reclass tax · ${input.jurisdictionLabel}` },
              { accountId: "2310", side: "credit", amount: input.amount, memo: taxMemo(input.jurisdictionId, input.note) },
            ],
          });
          setAccrualOpen(false);
        }}
      />

      <RemittanceDialog
        open={remitOpen}
        onClose={() => setRemitOpen(false)}
        defaultJurisdiction={remitJurisdiction}
        balances={summary.byJurisdiction}
        onPost={(input) => {
          postJournal({
            postingDate: input.date,
            description: `Sales tax remittance — ${input.jurisdictionLabel}`,
            source: "sales_tax",
            sourceRef: { kind: "sales_tax_remit", id: `rem_${Date.now()}` },
            createdBy: "Steve",
            lines: [
              { accountId: "2310", side: "debit", amount: input.amount, memo: taxMemo(input.jurisdictionId, input.reference) },
              { accountId: input.cashAccountId, side: "credit", amount: input.amount, memo: `Remit ${input.jurisdictionLabel} · ${input.reference ?? ""}`.trim() },
            ],
          });
          setRemitOpen(false);
        }}
      />
    </CockpitPage>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${tone ?? ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function AccrualDialog({
  open, onClose, onPost,
}: {
  open: boolean;
  onClose: () => void;
  onPost: (input: { date: string; jurisdictionId: string; jurisdictionLabel: string; amount: number; offsetAccountId: string; note?: string }) => void;
}) {
  const [date, setDate] = useState(today());
  const [jurisdictionId, setJurisdictionId] = useState(FL_JURISDICTIONS[0].id);
  const [amount, setAmount] = useState("");
  const [offsetAccountId, setOffsetAccountId] = useState("2200");
  const [note, setNote] = useState("");

  const jur = FL_JURISDICTIONS.find(j => j.id === jurisdictionId)!;
  const label = `${jur.countyName} County, FL`;
  const amt = parseFloat(amount) || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manual sales tax accrual</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            Posts <code>DR [offset] / CR 2310 Sales Tax</code> tagged to the jurisdiction.
            Use when tax was collected inside a customer payment but not auto-attributed —
            this carves the tax portion out of revenue/deferred-revenue into the liability.
            (Cash is already booked — do NOT double-debit it.)
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-8" />
            </div>
            <div>
              <Label className="text-xs">Amount</Label>
              <Input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" className="h-8" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Jurisdiction</Label>
            <select
              value={jurisdictionId}
              onChange={e => setJurisdictionId(e.target.value)}
              className="w-full h-8 text-sm rounded-md border border-border bg-background px-2"
            >
              {FL_JURISDICTIONS.map(j => (
                <option key={j.id} value={j.id}>{j.countyName} County — {(totalRate(j) * 100).toFixed(2)}%</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Reclassify FROM (offset)</Label>
              <select
                value={offsetAccountId}
                onChange={e => setOffsetAccountId(e.target.value)}
                className="w-full h-8 text-sm rounded-md border border-border bg-background px-2"
              >
                <option value="2200">2200 — Deferred Revenue</option>
                <option value="4030">4030 — Bathroom Revenue</option>
                <option value="4040">4040 — Kitchen Revenue</option>
                <option value="4050">4050 — Flooring Revenue</option>
                <option value="4060">4060 — Other Revenue</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Optional reference" className="h-8" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={amt <= 0}
            onClick={() => onPost({ date, jurisdictionId, jurisdictionLabel: label, amount: amt, offsetAccountId, note: note || undefined })}
          >
            Post accrual
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RemittanceDialog({
  open, onClose, defaultJurisdiction, balances, onPost,
}: {
  open: boolean;
  onClose: () => void;
  defaultJurisdiction: string;
  balances: Array<{ jurisdictionId: string; balance: number }>;
  onPost: (input: { date: string; jurisdictionId: string; jurisdictionLabel: string; amount: number; cashAccountId: string; reference?: string }) => void;
}) {
  const [date, setDate] = useState(today());
  const [jurisdictionId, setJurisdictionId] = useState(defaultJurisdiction || FL_JURISDICTIONS[0].id);
  const [amount, setAmount] = useState("");
  const [cashAccountId, setCashAccountId] = useState("1000");
  const [reference, setReference] = useState("");

  const jur = FL_JURISDICTIONS.find(j => j.id === jurisdictionId)!;
  const label = `${jur.countyName} County, FL`;
  const open_ = balances.find(b => b.jurisdictionId === jurisdictionId)?.balance ?? 0;
  const amt = parseFloat(amount) || 0;
  const exceeds = amt > open_ + 0.005;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record sales tax remittance</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            Posts <code>DR 2310 Sales Tax / CR Cash</code>. Tagged to the jurisdiction.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-8" />
            </div>
            <div>
              <Label className="text-xs">Amount</Label>
              <Input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" className="h-8" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Jurisdiction</Label>
            <select
              value={jurisdictionId}
              onChange={e => setJurisdictionId(e.target.value)}
              className="w-full h-8 text-sm rounded-md border border-border bg-background px-2"
            >
              {FL_JURISDICTIONS.map(j => (
                <option key={j.id} value={j.id}>{j.countyName} County</option>
              ))}
            </select>
            <div className="text-[11px] text-muted-foreground mt-1">
              Open balance: <span className={open_ > 0.005 ? "text-amber-600 dark:text-amber-500" : ""}>${open_.toFixed(2)}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Cash account</Label>
              <select
                value={cashAccountId}
                onChange={e => setCashAccountId(e.target.value)}
                className="w-full h-8 text-sm rounded-md border border-border bg-background px-2"
              >
                <option value="1000">1000 — Cash Operating</option>
                <option value="1010">1010 — Cash Payroll</option>
                <option value="1020">1020 — Cash Savings</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Reference</Label>
              <Input value={reference} onChange={e => setReference(e.target.value)} placeholder="Confirmation #" className="h-8" />
            </div>
          </div>
          {exceeds && (
            <div className="text-xs text-destructive flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Exceeds open balance for this jurisdiction.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={amt <= 0 || exceeds}
            onClick={() => onPost({ date, jurisdictionId, jurisdictionLabel: label, amount: amt, cashAccountId, reference: reference || undefined })}
          >
            Post remittance
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
